import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MuxUsageService } from 'src/app/services/mux-usage.service';
import { ConfigService } from 'src/app/services/config.service';

/**
 * Shared header status chips — auth status, usage quota, folder path, pinned messages badge.
 * Used by both the sync chat widget and heartbeat widget.
 */
@Component({
  selector: 'app-header-chips',
  templateUrl: './header-chips.component.html',
  styleUrls: ['./header-chips.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HeaderChipsComponent implements OnInit, OnDestroy, OnChanges {
  // --- Inputs ---

  /** Which backend to check auth/usage for (e.g. 'claude-cli', 'gemini'). */
  @Input() backendId: string = 'claude-cli';
  @Input() agentId: string | null = null;

  /** Path to show in folder chip (channel dir or turn dir). Null = hide chip. */
  @Input() folderPath: string | null = null;

  /** Short label for folder chip (e.g. "6e2dcf33" or "litai"). */
  @Input() folderLabel: string | null = null;

  /** Tooltip for folder chip. */
  @Input() folderTooltip: string = 'Click to copy path';

  /** Number of pinned messages. 0 = hide pin badge. */
  @Input() pinnedCount: number = 0;

  /** Whether pins pane is currently open (controls active highlight). */
  @Input() pinsPaneOpen: boolean = false;

  /** Whether to show backend auth/usage chips. False for channels (agent-agnostic). */
  @Input() showBackendChips: boolean = true;

  // --- Outputs ---

  /** User clicked folder chip — consumer handles clipboard copy. */
  @Output() copyPath = new EventEmitter<void>();

  /** User clicked pin badge. */
  @Output() togglePins = new EventEmitter<void>();

  /** User clicked auth chip. */
  @Output() openBackendDetail = new EventEmitter<void>();

  // --- Internal state ---

  backendAuthStatus: 'healthy' | 'warning' | 'error' | 'unknown' = 'unknown';
  backendAuthMessage: string = '';
  backendTokenExpiry: string | null = null;

  usageReport: MuxUsageService.UsageReport | null = null;
  usageRefreshing: boolean = false;

  private _subs = new Subscription();

  constructor(
    private _http: HttpClient,
    private _muxUsage: MuxUsageService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Subscribe to usage updates from the shared service
    this._subs.add(
      this._muxUsage.usage$.subscribe((report) => {
        this.usageReport = report;
        this.cdr.markForCheck();
      })
    );

    // Initial load
    if (this.backendId) {
      this.checkBackendAuthStatus();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['backendId'] && !changes['backendId'].firstChange) {
      this.checkBackendAuthStatus();
    }
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  // --- Usage ---

  get primaryUsageQuota(): MuxUsageService.UsageQuota | null {
    if (!this.usageReport?.available || !this.usageReport.quotas.length) {
      return null;
    }
    return this.usageReport.quotas.find(q => q.name === 'Session') || this.usageReport.quotas[0];
  }

  formatUsagePercentage(used: number): string {
    return MuxUsageService.formatPercentage(used);
  }

  getUsageChipClass(used: number): string {
    return MuxUsageService.getUsageClass(used, this.usageReport);
  }

  get costSummary(): string | null {
    if (!this.usageReport?.available) return null;
    const c30 = this.usageReport.estimated_cost_30d;
    const c7 = this.usageReport.estimated_cost_7d;
    if (c30 == null && c7 == null) return null;
    const fmt = (v: number) => v >= 1000 ? `$${(v/1000).toFixed(1)}k` : v >= 100 ? `$${Math.round(v)}` : `$${v.toFixed(2)}`;
    // Show 30d in the chip; 7d only in tooltip
    if (c30 != null) return `~${fmt(c30)}/mo`;
    if (c7 != null) return `~${fmt(c7)}/wk`;
    return null;
  }

  getUsageTooltip(): string {
    if (!this.usageReport?.available) {
      return 'Usage data unavailable';
    }

    const lines: string[] = [];
    for (const quota of this.usageReport.quotas) {
      const pct = MuxUsageService.formatPercentage(quota.used);
      const reset = quota.resets_at ? ` (resets in ${MuxUsageService.formatResetTime(quota.resets_at)})` : '';
      lines.push(`${quota.name}: ${pct} used${reset}`);
    }

    // Add cost estimate lines
    const c30 = this.usageReport.estimated_cost_30d;
    const c7 = this.usageReport.estimated_cost_7d;
    if (c30 != null || c7 != null) {
      lines.push(''); // blank separator line
      const fmt = (v: number) => v >= 1000 ? `$${(v/1000).toFixed(1)}k` : v >= 100 ? `$${Math.round(v)}` : `$${v.toFixed(2)}`;
      if (c30 != null) lines.push(`~${fmt(c30)} API value (30d)`);
      if (c7 != null) lines.push(`~${fmt(c7)} API value (7d)`);
    }

    return lines.join('\n');
  }

  async $refreshUsage(): Promise<void> {
    if (this.usageRefreshing) return;

    this.usageRefreshing = true;
    this.cdr.markForCheck();

    try {
      await this._muxUsage.fetchUsage(this.backendId, this.agentId);
      this._snackBar.open('Usage refreshed', '', {
        duration: 1500,
        horizontalPosition: 'center',
        verticalPosition: 'bottom'
      });
    } catch (error) {
      console.error('Failed to refresh usage:', error);
    } finally {
      this.usageRefreshing = false;
      this.cdr.markForCheck();
    }
  }

  /** Public — call from parent to force re-check auth status (e.g. after auth wizard completes). */
  public refreshAuthStatus(): void {
    this.checkBackendAuthStatus();
  }

  // --- Auth status ---

  private async checkBackendAuthStatus(): Promise<void> {
    const backendId = this.backendId;
    try {
      const response = await this._http.get<any>(`${ConfigService.muxUrl}/backends/${backendId}/status`).toPromise();

      if (response.auth_status === 'authenticated') {
        if (response.warning) {
          this.backendAuthStatus = 'warning';
          this.backendAuthMessage = response.warning;
        } else {
          this.backendAuthStatus = 'healthy';
          this.backendAuthMessage = 'Authenticated';
        }
        if (response.token_details?.expires_in) {
          this.backendTokenExpiry = response.token_details.expires_in;
        }
      } else if (response.auth_status === 'token_expired') {
        this.backendAuthStatus = 'error';
        this.backendAuthMessage = response.error || 'Token expired';
        this.backendTokenExpiry = null;
      } else if (response.auth_status === 'not_authenticated' || response.auth_status === 'not_installed' || response.auth_status === 'not_configured') {
        this.backendAuthStatus = 'error';
        this.backendAuthMessage = response.error || 'Not configured';
        this.backendTokenExpiry = null;
      } else if (response.configured && response.healthy) {
        this.backendAuthStatus = 'healthy';
        this.backendAuthMessage = 'Configured';
        this.backendTokenExpiry = null;
      } else if (response.configured && !response.healthy) {
        this.backendAuthStatus = 'error';
        this.backendAuthMessage = response.error || 'Health check failed';
        this.backendTokenExpiry = null;
      } else if (!response.configured) {
        this.backendAuthStatus = 'error';
        this.backendAuthMessage = response.error || 'Not configured';
        this.backendTokenExpiry = null;
      } else {
        this.backendAuthStatus = 'unknown';
        this.backendAuthMessage = '';
        this.backendTokenExpiry = null;
      }
    } catch (error) {
      console.error(`Failed to check backend auth status for ${backendId}:`, error);
      this.backendAuthStatus = 'unknown';
      this.backendAuthMessage = '';
      this.backendTokenExpiry = null;
    }
    this.cdr.markForCheck();
  }

  getAuthStatusIcon(): string {
    switch (this.backendAuthStatus) {
      case 'healthy': return 'check_circle';
      case 'warning': return 'warning';
      case 'error': return 'error';
      default: return 'help';
    }
  }

  getAuthStatusTooltip(): string {
    const backendId = this.backendId;
    if (this.backendAuthStatus === 'healthy' && this.backendTokenExpiry) {
      return `${backendId}: Authenticated\nExpires: ${this.backendTokenExpiry}`;
    }
    if (this.backendAuthStatus === 'healthy') {
      return `${backendId}: ${this.backendAuthMessage}`;
    }
    if (this.backendAuthStatus === 'warning') {
      return `${backendId}: ${this.backendAuthMessage}\nExpires: ${this.backendTokenExpiry || 'unknown'}`;
    }
    if (this.backendAuthStatus === 'error') {
      return `${backendId}: ${this.backendAuthMessage}\nClick to configure`;
    }
    return `${backendId}: Status unknown\nClick to configure`;
  }
}
